'use client';

import { Button } from '@shared/components/ui/button';
import { Combobox } from '@shared/components/ui/AdvancedFilter/components';
import { Input } from '@shared/components/ui';
import { MultiSelect } from '@shared/components/ui/MultiSelect';
import type { LoadOptionsResponse } from '@shared/components/ui/MultiSelect';
import { DateRangePicker } from '@shared/components/ui/DateRangePicker';
import { cn } from '@shared/utils/utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TagModel } from '../../models/CRM';
import type { DateRange } from 'react-day-picker';

/**
 * Formatea una fecha a formato Y-m-d para la API (usa componentes locales)
 */
function formatDateForAPI(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Parsea una cadena Y-m-d como fecha local (evita desfase de un día por UTC)
 */
function parseLocalDate(dateStr: string): Date | undefined {
  if (!dateStr?.trim()) return undefined;
  const match = dateStr.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return undefined;
  const [, y, m, d] = match.map(Number);
  const date = new Date(y, m - 1, d);
  return isNaN(date.getTime()) ? undefined : date;
}

interface CRMFiltersPanelProps {
  /** Clientes seleccionados (array de IDs como strings) */
  teamIds?: string[];
  /** Búsqueda por teléfono; se combina en param search */
  phoneSearchTerm?: string;
  /** Tiendas seleccionadas (array de IDs como strings) */
  shopIds?: string[];
  /** Tag seleccionado */
  tagId?: number;
  /** Países de destino seleccionados (array de códigos ISO2 como strings) */
  shippingCountryCodes?: string[];
  /** Métodos de pago seleccionados (array de IDs como strings) */
  paymentMethodIds?: string[];
  /** Productos seleccionados (array de IDs como strings) */
  productIds?: string[];
  /** Fecha desde (Y-m-d) */
  dateFrom?: string;
  /** Fecha hasta (Y-m-d) */
  dateTo?: string;
  /** Función para cargar clientes con paginación y búsqueda (para MultiSelect dinámico) */
  loadTeamsForMultiSelect: (params: { search: string; page: number; limit: number }) => Promise<LoadOptionsResponse>;
  /** Función para cargar tiendas con paginación y búsqueda (para MultiSelect dinámico) */
  loadShopsForMultiSelect: (params: { search: string; page: number; limit: number }) => Promise<LoadOptionsResponse>;
  /** Función para cargar productos con paginación y búsqueda (para MultiSelect dinámico) */
  loadProductsForMultiSelect: (params: { search: string; page: number; limit: number }) => Promise<LoadOptionsResponse>;
  /** Función para cargar países con paginación y búsqueda (para MultiSelect dinámico) */
  loadCountriesForMultiSelect: (params: {
    search: string;
    page: number;
    limit: number;
  }) => Promise<LoadOptionsResponse>;
  /** Opciones de métodos de pago */
  paymentMethodOptions: Array<{ id: string; name: string }>;
  /** Lista de tags */
  tags: TagModel[];
  /** Si los métodos de pago están cargando */
  isLoadingPaymentMethods?: boolean;
  /** Si los tags están cargando */
  isLoadingTags?: boolean;
  /** Handlers */
  onShopIdsChange: (shopIds: string[]) => void;
  onTagChange: (tagId: number | undefined) => void;
  onShippingCountryCodesChange: (codes: string[]) => void;
  onPaymentMethodChange: (paymentMethodIds: string[]) => void;
  onTeamIdsChange: (teamIds: string[]) => void;
  onPhoneSearchChange: (value: string) => void;
  onProductIdsChange: (productIds: string[]) => void;
  onDateRangeChange: (dateFrom: string, dateTo: string) => void;
  onClearAll: () => void;
  /** Clases CSS adicionales */
  className?: string;
}

/**
 * CRMFiltersPanel - Panel de filtros para la lista de pedidos CRM
 * Filtros: Tienda, Tag, Método de pago, Rango de fechas (date_from/date_to en URL y API)
 */
export const CRMFiltersPanel = ({
  teamIds = [],
  phoneSearchTerm = '',
  shopIds = [],
  tagId,
  shippingCountryCodes = [],
  paymentMethodIds = [],
  productIds = [],
  dateFrom,
  dateTo,
  loadTeamsForMultiSelect,
  loadShopsForMultiSelect,
  loadProductsForMultiSelect,
  loadCountriesForMultiSelect,
  paymentMethodOptions,
  tags,
  isLoadingPaymentMethods = false,
  isLoadingTags = false,
  onShopIdsChange,
  onTagChange,
  onShippingCountryCodesChange,
  onPaymentMethodChange,
  onTeamIdsChange,
  onPhoneSearchChange,
  onProductIdsChange,
  onDateRangeChange,
  onClearAll,
  className,
}: CRMFiltersPanelProps) => {
  const { t } = useTranslation('crmModule');

  const tagOptions = useMemo(() => {
    const all = [{ value: '', label: t('filters.allTags', 'Todas las etiquetas') }];
    const opts = tags.map((tag) => ({
      value: String(tag.id),
      label: tag.name,
    }));
    return [...all, ...opts];
  }, [tags, t]);

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (teamIds.length > 0) count++;
    if (phoneSearchTerm?.trim()) count++;
    if (shopIds.length > 0) count++;
    if (tagId !== undefined) count++;
    if (shippingCountryCodes.length > 0) count++;
    if (paymentMethodIds.length > 0) count++;
    if (productIds.length > 0) count++;
    if (dateFrom && dateTo) count++;
    return count;
  }, [teamIds, phoneSearchTerm, shopIds, tagId, shippingCountryCodes, paymentMethodIds, productIds, dateFrom, dateTo]);

  // Opciones de métodos de pago para MultiSelect
  const paymentMethodOptionsForMultiSelect = useMemo(() => {
    return paymentMethodOptions.map((method) => ({
      value: method.id,
      label: method.name,
    }));
  }, [paymentMethodOptions]);

  // paymentMethodIds ya es un array de strings, perfecto para MultiSelect
  const paymentMethodValue = useMemo(() => {
    return paymentMethodIds || [];
  }, [paymentMethodIds]);

  const productValue = useMemo(() => productIds || [], [productIds]);

  // Parsear fechas Y-m-d como local para que el calendario no muestre un día menos (UTC)
  const dateFromParsed = dateFrom ? parseLocalDate(dateFrom) : undefined;
  const dateToParsed = dateTo ? parseLocalDate(dateTo) : undefined;

  const handleDateRangeUpdate = (range: DateRange | undefined) => {
    if (range?.from && range?.to) {
      onDateRangeChange(formatDateForAPI(range.from), formatDateForAPI(range.to));
    } else {
      onDateRangeChange('', '');
    }
  };

  const handleTagSelect = (value: string) => {
    onTagChange(value ? Number(value) : undefined);
  };

  // Mismo aspecto que el Input (Cliente): fondo y borde unificados para todos los controles
  const inputLikeTriggerClass = 'border-card-border bg-card-bg h-10';

  return (
    <div className={cn('space-y-5', className)}>
      {/* Sección de filtros - Sección de filtros */}
      <div className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Cliente - Client (team-child) */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">
              {t('filters.client.placeholder', 'Cliente')}
            </label>
            <MultiSelect
              loadOptions={loadTeamsForMultiSelect}
              value={teamIds}
              onValueChange={onTeamIdsChange}
              placeholder="Todos los clientes"
              emptyText={t('filters.notFound', 'Sin resultados')}
              noResultsText={t('filters.notFound', 'Sin resultados')}
              dataTestId="filter-crm-client"
              searchPlaceholder="Buscar clientes..."
              triggerClassName={inputLikeTriggerClass}
              pageSize={20}
            />
          </div>

          {/* Teléfono - Phone; mismo param search */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">
              {t('filters.phone.placeholder', 'Teléfono')}
            </label>
            <Input
              value={phoneSearchTerm}
              onChange={(e) => onPhoneSearchChange(e.target.value)}
              placeholder={t('filters.phone.placeholder', 'Teléfono')}
              className="h-10"
              data-testid="filter-crm-phone"
            />
          </div>

          {/* Tienda - Shop */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">
              {t('filters.shop.placeholder', 'Tienda')}
            </label>
            <MultiSelect
              loadOptions={loadShopsForMultiSelect}
              value={shopIds}
              onValueChange={onShopIdsChange}
              placeholder="Todas las tiendas"
              emptyText={t('filters.notFound', 'Sin resultados')}
              noResultsText={t('filters.notFound', 'Sin resultados')}
              dataTestId="filter-crm-shop"
              searchPlaceholder="Buscar tiendas..."
              triggerClassName={inputLikeTriggerClass}
              pageSize={20}
            />
          </div>

          {/* Tag - Tag */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">
              {t('filters.tag.placeholder', 'Etiqueta')}
            </label>
            <Combobox
              options={tagOptions}
              value={tagId !== undefined ? String(tagId) : ''}
              setValue={handleTagSelect}
              placeholder={
                isLoadingTags ? t('loading.message', 'Cargando...') : t('filters.tag.placeholder', 'Etiqueta')
              }
              disabled={isLoadingTags}
              buttonClassName={inputLikeTriggerClass}
              dataTestId="filter-crm-tag"
            />
          </div>

          {/* País de destino - Destination country */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">
              {t('filters.shipping_country.placeholder', 'País de destino')}
            </label>
            <MultiSelect
              loadOptions={loadCountriesForMultiSelect}
              value={shippingCountryCodes}
              onValueChange={onShippingCountryCodesChange}
              placeholder="Todos los países"
              emptyText={t('filters.notFound', 'Sin resultados')}
              noResultsText={t('filters.notFound', 'Sin resultados')}
              dataTestId="filter-crm-destination-country"
              searchPlaceholder="Buscar países..."
              triggerClassName={inputLikeTriggerClass}
              pageSize={20}
            />
          </div>

          {/* Método de pago - Payment Method */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">
              {t('filters.paymentMethod', 'Método de pago')}
            </label>
            <MultiSelect
              options={paymentMethodOptionsForMultiSelect}
              value={paymentMethodValue}
              onValueChange={onPaymentMethodChange}
              placeholder={isLoadingPaymentMethods ? 'Cargando...' : 'Todos'}
              emptyText={t('filters.notFound', 'Sin resultados')}
              disabled={isLoadingPaymentMethods}
              dataTestId="filter-crm-payment-method"
              triggerClassName={inputLikeTriggerClass}
            />
          </div>

          {/* Productos - Products */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">
              {t('filters.products.placeholder', 'Productos')}
            </label>
            <MultiSelect
              loadOptions={loadProductsForMultiSelect}
              value={productValue}
              onValueChange={onProductIdsChange}
              placeholder={t('filters.products.placeholder', 'Productos')}
              emptyText={t('filters.notFound', 'Sin resultados')}
              noResultsText={t('filters.notFound', 'Sin resultados')}
              dataTestId="filter-crm-products"
              searchPlaceholder={t('filters.products.searchPlaceholder', 'Buscar productos...')}
              triggerClassName={inputLikeTriggerClass}
              pageSize={20}
            />
          </div>

          {/* Rango de fechas - Date range */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">
              {t('filters.dateRange', 'Rango de fechas')}
            </label>
            <DateRangePicker
              initialDateFrom={dateFromParsed}
              initialDateTo={dateToParsed}
              onUpdate={({ range }) => handleDateRangeUpdate(range)}
              placeholder={t('filters.dateRangePlaceholder', 'Seleccionar...')}
              dataTestId="filter-crm-date-range"
              triggerClassName={inputLikeTriggerClass}
            />
          </div>
        </div>
      </div>

      {/* Footer con acciones - Footer with actions */}
      {activeFiltersCount > 0 && (
        <div className="flex items-center justify-end pt-3 border-t border-card-border">
          <Button
            variant="ghost"
            size="sm"
            className="text-sm text-muted-foreground hover:text-foreground"
            onClick={onClearAll}
            type="button"
            data-testid="filter-crm-clear-all"
          >
            {`${t('filters.clearLabel', 'Limpiar')} ${activeFiltersCount} ${activeFiltersCount === 1 ? t('filters.filterSingular', 'filtro') : t('filters.filterPlural', 'filtros')}`}
          </Button>
        </div>
      )}
    </div>
  );
};
